import { DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { Component, computed, Inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Store } from '@ngxs/store';
import { DebtScheduleItem } from 'ng2-app/models/dscr/DebtScheduleItem.struct';
import { DebtScheduleService } from 'ng2-app/services/debt-schedule/debt-schedule.service';
import { BorrowerState } from 'ng2-app/store/borrower/borrower/borrower.state';
import { CreateDebtScheduleItem, UpdateDebtScheduleItem } from 'ng2-app/store/debt-schedule/debt-schedule.actions';

@Component({
  selector: 'app-debt-schedule-item-dialog',
  standalone: true,
  template: `
    <h2 class="!tw-mb-0" mat-dialog-title>
      {{ titleVariant() }} Debt Schedule Item
    </h2>

    <mat-dialog-content>

      <form class="tw-pt-2" [formGroup]="formGroup">
        <mat-form-field class="tw-w-full">
          <mat-label>Lender/Debt Name</mat-label>
          <input matInput [formControlName]="'lenderName'" [type]="'text'" />
        </mat-form-field>

        <mat-form-field class="tw-w-full">
          <mat-label>Type</mat-label>
          <mat-select [formControlName]="'debtType'">
            <mat-option *ngFor="let option of debtTypeOptions" [value]="option.value">
              {{option.label}}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field class="tw-w-full">
          <mat-label>Loan Start Date</mat-label>
          <input matInput [formControlName]="'loanStartDate'" [matDatepicker]="picker">
          <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-datepicker #picker></mat-datepicker>
        </mat-form-field>

        <mat-form-field class="tw-w-full">
          <mat-label>Term</mat-label>
          <input matInput [formControlName]="'term'" [type]="'number'" />
        </mat-form-field>

        <mat-form-field class="tw-w-full">
          <mat-label>Type</mat-label>
          <mat-select [formControlName]="'paymentFrequency'">
            <mat-option *ngFor="let option of frequencyOptions" [value]="option.value">
              {{option.label}}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field class="tw-w-full">
          <mat-label>Interest Rate</mat-label>
          <input matInput [formControlName]="'interestRate'" [type]="'number'" />
        </mat-form-field>

        <mat-form-field class="tw-w-full">
          <mat-label>Original Balance</mat-label>
          <input matInput [formControlName]="'originalBalance'" [type]="'number'" />
        </mat-form-field>

        <mat-form-field class="tw-w-full">
          <mat-label>Current Balance</mat-label>
          <input matInput [formControlName]="'currentBalance'" [type]="'number'" />
        </mat-form-field>

        <mat-form-field class="tw-w-full">
          <mat-label>Payment Amount</mat-label>
          <input matInput [formControlName]="'paymentAmount'" [type]="'number'" />
        </mat-form-field>

        <mat-checkbox [formControlName]="'isDebtConsolidation'">
          <mat-label>Is Debt Consolidation</mat-label>
        </mat-checkbox>
      </form>

    </mat-dialog-content>

    <mat-dialog-actions class="tw-p-2" [align]="'end'">
      <button mat-stroked-button mat-dialog-close>Cancel</button>
      <button mat-flat-button [color]="'primary'" (click)="save()">Save</button>
    </mat-dialog-actions>

  `,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    FormsModule,
    MatDatepickerModule,
    MatSelectModule,
  ],
})
export class DebtScheduleItemDialogComponent {
  borrowerId = toSignal( this.store.select( BorrowerState.borrowerId ) );
  formGroup: FormGroup;


  debtTypeOptions = DebtScheduleService.debtTypeOptions;
  frequencyOptions = DebtScheduleService.paymentFrequencyOptions;

  editMode = computed(() => !!this.data?.id);
  titleVariant = computed(() => {
    return this.editMode() ? 'Edit' : 'Create';
  });

  constructor(
    private store: Store,
    private dialogRef: DialogRef<DebtScheduleItemDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data?: DebtScheduleItem,
  ) {
    this.formGroup = new FormGroup({
      lenderName: new FormControl(this.data?.lenderName, [Validators.required]),
      debtType: new FormControl(this.data?.debtType, [Validators.required]),
      loanStartDate: new FormControl(this.data?.loanStartDate, [Validators.required]),
      paymentFrequency: new FormControl(this.data?.paymentFrequency, [Validators.required]),
      originalBalance: new FormControl(this.data?.originalBalance, [Validators.required]),
      currentBalance: new FormControl(this.data?.currentBalance, [Validators.required]),
      paymentAmount: new FormControl(this.data?.paymentAmount, [Validators.required]),
      isDebtConsolidation: new FormControl(this.data?.isDebtConsolidation),
      term: new FormControl(this.data?.term, [Validators.required]),
      interestRate: new FormControl(this.data?.interestRate, [Validators.required]),
    });
  }

  save() {
    const action = this.editMode() ? UpdateDebtScheduleItem : CreateDebtScheduleItem;
    const borrowerId = this.borrowerId() as number;
    const id = this.data?.id as number;
    const value = {
      id: id,
      ...this.formGroup.value,
      borrowerId,
    } as DebtScheduleItem;
    this.store.dispatch( new action( borrowerId, value, id ) );
    this.dialogRef.close();
  }
}
